import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useApi, configApiRef } from '@backstage/core-plugin-api';
import { usePendingApprovals } from '../../../../../packages/app/src/hooks/useApprovals';
import { Page, Content, Progress } from '@backstage/core-components';
import { Box, Card, CardContent, Typography, Snackbar } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  CheckCircle as ApproveIcon,
  SupervisorAccount as SuperUserIcon,
} from '@material-ui/icons';
import { EmptyState } from '../shared';
import { useUserIdentity } from '../../hooks';
import { ApprovalsTable } from './components/ApprovalsTable';
import { StatsCard } from './components/StatsCard';
import { ConfirmDialog } from './components/ConfirmDialog';
import { Breadcrumbs, ErrorToast, InlineError } from '@internal/backstage-plugin-global-header';

const useStyles = makeStyles(theme => ({
  card: {
    marginBottom: theme.spacing(3),
    borderRadius: theme.spacing(2),
    boxShadow: '0 2px 12px rgba(0,0,0,0.05)',
  },
  headerContent: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  approvalsIcon: {
    marginRight: theme.spacing(1),
    color: '#6366f1',
  },
  superUserNote: {
    backgroundColor: '#f3e8ff',
    border: '1px solid #c084fc',
    borderRadius: theme.spacing(1),
    padding: theme.spacing(1.5),
    marginTop: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
    color: '#7c3aed',
  },
}));

interface AccessRequest {
  id: number;
  product: string;
  requested_by: string;
  role: string;
  requested_at: string;
  status: 'pending' | 'approved' | 'rejected';
  approved_by: string | null;
  approved_at: string | null;
}

interface UserPermission {
  product: string;
  permission: string;
}

interface ApiResponse {
  user_permissions: Array<Record<string, string>>;
  pending_approvals: Array<Record<string, string>>;
}

const ApprovalsPage: React.FC = () => {
  const { refresh } = usePendingApprovals();
  const classes = useStyles();
  const { fetchUserIdentity } = useUserIdentity();
  const [requests, setRequests] = useState<AccessRequest[]>([]);
  const [userPermissions, setUserPermissions] = useState<UserPermission[]>([]);
  const [loading, setLoading] = useState(true);
  const [requestError, setRequestError] = useState<string | null>(null);
  const [permError, setPermError] = useState<string | null>(null);
  const [hasAicoeSuperUser, setHasAicoeSuperUser] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    request: AccessRequest | null;
    action: 'approve' | 'reject';
  }>({
    open: false,
    request: null,
    action: 'approve',
  });

  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const configApi = useApi(configApiRef);
  const baseUrl = configApi.getString('backend.baseUrl');
  const baseUrlRef = useRef(baseUrl);

  const ownerProducts = useMemo((): string[] => {
    return userPermissions
      .filter(perm => perm.permission.toLowerCase() === 'admin')
      .map(perm => perm.product);
  }, [userPermissions]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setRequestError(null);
    setPermError(null);
    try {
      const { email, token } = await fetchUserIdentity();

      const [permResult, reqResult] = await Promise.allSettled([
        fetch(
          `${baseUrlRef.current}/api/custom-api/rbac?user_mail=${email}`,
          {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          },
        ),
        fetch(
          `${baseUrlRef.current}/api/custom-api/approval?user_mail=${email}`,
          {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          },
        ),
      ]);

      // Permissions: secondary data — its failure only limits manage actions,
      // so it degrades on its own without blocking the requests table.
      if (permResult.status === 'fulfilled' && permResult.value.ok) {
        const rolesData: ApiResponse[] = await permResult.value.json();
        const userRoles = Array.isArray(rolesData[0]?.user_permissions)
          ? rolesData[0].user_permissions
          : [];
        const userPermissionsMap: Record<string, string> = {};
        let hasAicoeMarketplaceSuperUser = false;

        userRoles.forEach(permissionObj => {
          Object.entries(permissionObj).forEach(([product, permission]) => {
            userPermissionsMap[product] = permission;
            if (
              permission.toLowerCase() === 'super_admin' &&
              product.toLowerCase() === 'aicoe_marketplace'
            ) {
              hasAicoeMarketplaceSuperUser = true;
            }
          });
        });

        setHasAicoeSuperUser(hasAicoeMarketplaceSuperUser);
        setUserPermissions(
          Object.entries(userPermissionsMap).map(([product, permission]) => ({
            product,
            permission,
          })),
        );
      } else {
        setPermError(
          "Couldn't load your permissions — some actions may be limited.",
        );
      }

      // Requests: primary content — its failure blocks the table.
      if (reqResult.status === 'fulfilled' && reqResult.value.ok) {
        const requestsData = await reqResult.value.json();
        setRequests(requestsData.rows || []);
      } else {
        setRequestError('Access requests could not be loaded');
      }

      await refresh();
    } catch {
      // Identity/auth failure — neither call could run. Keep raw error text out
      // of the UI; show clean, resource-specific copy instead.
      setRequestError('Access requests could not be loaded');
      setPermError(
        "Couldn't load your permissions — some actions may be limited.",
      );
    } finally {
      setLoading(false);
    }
  }, [fetchUserIdentity, refresh]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleApprove = (request: AccessRequest) => {
    setConfirmDialog({ open: true, request, action: 'approve' });
  };

  const handleReject = (request: AccessRequest) => {
    setConfirmDialog({ open: true, request, action: 'reject' });
  };

  const confirmAction = async (reason?: string) => {
    if (!confirmDialog.request) return;

    try {
      const currentTime = new Date().toISOString();
      const requestId = confirmDialog.request.id;
      const newStatus = confirmDialog.action === 'approve' ? 'approved' : 'rejected';

      const { email, token } = await fetchUserIdentity();

      const response = await fetch(
        `${baseUrlRef.current}/api/custom-api/approval/${requestId}`,
        {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            status: newStatus,
            approved_by: email,
            approved_at: currentTime,
            approver_reason: reason || 'approved',
          }),
        },
      );

      if (!response.ok) {
        if (response.status === 409) {
          setActionError('This request was already processed by another admin.');
          setConfirmDialog({ open: false, request: null, action: 'approve' });
          const { email: refreshEmail, token: refreshToken } = await fetchUserIdentity();
          const reqResponse = await fetch(
            `${baseUrlRef.current}/api/custom-api/approval?user_mail=${refreshEmail}`,
            { headers: { Authorization: `Bearer ${refreshToken}`, 'Content-Type': 'application/json' } },
          );
          const requestsData = await reqResponse.json();
          setRequests(requestsData.rows || []);
          await refresh();
          return;
        }
        throw new Error(`Failed to ${confirmDialog.action} request`);
      }

      setSuccessMessage(`Request ${newStatus} successfully.`);
      setConfirmDialog({ open: false, request: null, action: 'approve' });
      const { email: refreshEmail, token: refreshToken } = await fetchUserIdentity();
      const reqResponse = await fetch(
        `${baseUrlRef.current}/api/custom-api/approval?user_mail=${refreshEmail}`,
        { headers: { Authorization: `Bearer ${refreshToken}`, 'Content-Type': 'application/json' } },
      );
      const requestsData = await reqResponse.json();
      setRequests(requestsData.rows || []);
      await refresh();
    } catch (err) {
      setActionError(`Failed to ${confirmDialog.action} request. Please try again.`);
      setConfirmDialog({ open: false, request: null, action: 'approve' });
    }
  };

  const handleSnackbarClose = (_?: unknown, reason?: string) => {
    if (reason === 'clickaway') return;
    setSuccessMessage(null);
  };

  const getRequestStats = () => {
    const stats = requests.reduce((acc, request) => {
      acc[request.status] = (acc[request.status] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return [
      { label: 'Pending', count: stats.pending || 0, color: '#f39c12' },
      { label: 'Approved', count: stats.approved || 0, color: '#27ae60' },
      { label: 'Rejected', count: stats.rejected || 0, color: '#e74c3c' },
      { label: 'Total', count: requests.length, color: '#6366f1' },
    ];
  };

  const canManageRequest = (request: AccessRequest): boolean => {
    return hasAicoeSuperUser || ownerProducts.includes(request.product);
  };

  if (loading) return <Progress />;

  if (requestError) {
    return (
      <Page themeId="tool">
        <Content>
          <Breadcrumbs />
          <Box mt={2}>
            <InlineError
              title="Access requests could not be loaded"
              description="Your submitted requests are safe and unaffected."
              onRetry={loadData}
            />
          </Box>
        </Content>
      </Page>
    );
  }

  return (
    <Page themeId="tool">
      <Content>
        <Breadcrumbs />
        <Card className={classes.card}>
          <CardContent>
            <div className={classes.headerContent}>
              <ApproveIcon className={classes.approvalsIcon} />
              <Typography variant="h5" style={{ fontWeight: 600, color: '#374151' }}>
                Access Request Approvals
              </Typography>
            </div>

            <Typography variant="body1" color="textSecondary" paragraph>
              {hasAicoeSuperUser
                ? 'As a Super Admin, you can view and manage all access requests across all products.'
                : 'Review and manage access requests for products where you have admin permissions.'}
            </Typography>

            {hasAicoeSuperUser && (
              <div className={classes.superUserNote}>
                <SuperUserIcon style={{ marginRight: 8 }} />
                <Typography variant="body2">
                  Super Admin mode: Viewing all requests system-wide
                </Typography>
              </div>
            )}

            <StatsCard stats={getRequestStats()} />
          </CardContent>
        </Card>

        {requests.length === 0 ? (
          <EmptyState
            title="📋 No Access Requests"
            message={
              hasAicoeSuperUser
                ? 'There are currently no access requests in the system.'
                : 'There are currently no access requests for the products you own.'
            }
          />
        ) : (
          <ApprovalsTable
            requests={requests}
            canManageRequest={canManageRequest}
            onApprove={handleApprove}
            onReject={handleReject}
          />
        )}

        <ConfirmDialog
          open={confirmDialog.open}
          action={confirmDialog.action}
          requestedBy={confirmDialog.request?.requested_by}
          role={confirmDialog.request?.role}
          product={confirmDialog.request?.product}
          onClose={() =>
            setConfirmDialog({ open: false, request: null, action: 'approve' })
          }
          onConfirm={confirmAction}
        />

        <Snackbar
          open={!!successMessage}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          message={successMessage}
          ContentProps={{ style: { backgroundColor: '#4caf50', color: 'white', fontWeight: 500 } }}
        />
        <ErrorToast message={actionError} onClose={() => setActionError(null)} />
        <ErrorToast message={permError} onClose={() => setPermError(null)} />
      </Content>
    </Page>
  );
};

export default ApprovalsPage;
